import { create } from 'zustand';
import useAuthStore from '../authStore';
import useSchoolStore from '../schoolStore';
import * as repo from '../../repositories/reports';
import ReportItem from '../../models/report';
import { Result } from '../../models/result';

export const useReportStore = create((set, get) => ({
    // Class Performance Reports
    reports: [],
    reportItemsList: [],
    isLoadingReports: false,
    isLoadingReportItems: false,
    error: null,
    
    // Single Student Report state
    selectedStudent: null,
    studentReport: null,
    isLoadingStudentReport: false,
    studentReportError: null,

    // Multiple student reports list (for class/batch views)
    studentReportsList: [],
    studentReportsMap: {},
    isLoadingStudentReportsList: false,
    studentReportsListError: null,

    getReports: async (schoolId) => {
        const { userToken } = useAuthStore.getState();
        set({ isLoadingReports: true, error: null });
        
        try {
            const result = await repo.getReports({ schoolId, userToken });
            
            if (!result?.success) {
                set({ error: result?.message || 'Failed to fetch', isLoadingReports: false });
                return result;
            }
            
            set({ reports: result.value || [], isLoadingReports: false, error: null });
            return result;
        } catch (error) {
            set({ error: error.message, isLoadingReports: false });
            return new Result({ message: error.message });
        }
    },
    
    getReportItems: async (reportId, schoolId) => {
        const { userToken } = useAuthStore.getState();
        set({ isLoadingReportItems: true, error: null });
        
        try {
            const result = await repo.getReportItems({ reportId, schoolId, userToken });
            
            if (!result?.success) {
                set({ error: result?.message || 'Failed to fetch', isLoadingReportItems: false });
                return result;
            }
            
            let items = [];
            if(Array.isArray(result.value)) {
                items = result.value.map(item => ReportItem.fromJson(item, reportId));
            }
            
            set({ reportItemsList: items, isLoadingReportItems: false, error: null });
            return result;
        } catch (error) {
            set({ error: error.message, isLoadingReportItems: false });
            return new Result({ message: error.message });
        }
    },

    /**
     * Retrieves a single student report by userId or admissionNo with Class, sec and academicYear filters
     * @param {Object} params - { schoolId, userId, admissionNo, Class, sec, academicYear }
     */
    getStudentReport: async ({ schoolId, userId, admissionNo, Class, sec, academicYear } = {}) => {
        set({ isLoadingStudentReport: true, studentReportError: null });
        try {
            const schoolState = useSchoolStore.getState();
            const currentAcademicYear = academicYear || 
                schoolState.currentAcademicYear || 
                schoolState.schoolData?.currentAcademicYear || '';

            const result = await repo.getStudentReport({ 
                schoolId: schoolId || schoolState.schoolId, 
                userId, 
                admissionNo,
                Class,
                sec,
                academicYear: currentAcademicYear
            });
            
            if (!result?.success || !result?.value) {
                const message = result?.message || 'No report found for this student';
                set({ 
                    studentReport: null, 
                    studentReportError: message, 
                    isLoadingStudentReport: false 
                });
                return result;
            }

            set({ 
                studentReport: result.value, 
                isLoadingStudentReport: false, 
                studentReportError: null 
            });
            return result;
        } catch (error) {
            const message = error.message || 'Error fetching student report';
            set({ 
                studentReport: null, 
                studentReportError: message, 
                isLoadingStudentReport: false 
            });
            return new Result({ message });
        }
    },

    /**
     * Retrieves student reports for a class and section with academicYear
     * @param {Object} params - { schoolId, Class, sec, academicYear, userIds }
     */
    getStudentReports: async ({ schoolId, Class, sec, academicYear, userIds } = {}) => {
        set({ isLoadingStudentReportsList: true, studentReportsListError: null });
        try {
            const schoolState = useSchoolStore.getState();
            const currentAcademicYear = academicYear || 
                schoolState.currentAcademicYear || 
                schoolState.schoolData?.currentAcademicYear || '';

            const result = await repo.getStudentReports({ 
                schoolId: schoolId || schoolState.schoolId, 
                Class, 
                sec, 
                academicYear: currentAcademicYear, 
                userIds 
            });

            if (result?.success) {
                const reportsList = result.value || [];
                const reportsMap = {};
                reportsList.forEach(r => {
                    if (r.userId) reportsMap[r.userId] = r;
                    if (r.admissionNo) reportsMap[r.admissionNo] = r;
                });

                set({ 
                    studentReportsList: reportsList,
                    studentReportsMap: reportsMap,
                    isLoadingStudentReportsList: false, 
                    studentReportsListError: null 
                });
            } else {
                set({ 
                    studentReportsList: [], 
                    studentReportsMap: {},
                    isLoadingStudentReportsList: false, 
                    studentReportsListError: result?.message || 'Failed to fetch reports' 
                });
            }
            return result;
        } catch (error) {
            set({ 
                studentReportsList: [], 
                studentReportsMap: {},
                isLoadingStudentReportsList: false, 
                studentReportsListError: error.message 
            });
            return new Result({ message: error.message });
        }
    },

    /**
     * Sets active selected student
     * @param {Object|null} student
     */
    setSelectedStudent: (student) => set({ selectedStudent: student }),

    /**
     * Sets active student report in store directly (e.g. when clicked from student list)
     * @param {StudentReport} report 
     */
    setSelectedStudentReport: (report) => set({
        studentReport: report,
        studentReportError: null,
        isLoadingStudentReport: false
    }),

    /**
     * Clears active student report and selected student from store
     */
    clearStudentReport: () => set({ 
        studentReport: null, 
        selectedStudent: null,
        studentReportError: null, 
        isLoadingStudentReport: false 
    })
}));
